import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { helpApi } from '../services/api';
import { useSettings } from '../context/SettingsContext';
import { usePlan } from '../hooks/usePlan';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Markdown } from '../components/Markdown';
import {
  Search,
  ArrowLeft,
  ChevronRight,
  BookOpen,
  Loader2,
  Sparkles,
  LifeBuoy,
} from 'lucide-react';

const CATEGORY_ICONS = {
  'Getting Started': '🚀',
  'Assets & Fleet': '🚜',
  'Inspections': '📋',
  'Defects': '🔧',
  'Work Orders': '🛠️',
  'Maintenance': '🗓️',
  'Users & Permissions': '👥',
  'Compliance & Certificates': '🛡️',
  'Mobile App': '📱',
  'Billing & Plans': '💳',
};


/**
 * Help Centre landing — search + category sidebar + article cards.
 */
export const HelpCentrePage = () => {
  const navigate = useNavigate();
  const { isPro } = usePlan();
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([helpApi.listArticles(), helpApi.categories()])
      .then(([artRes, catRes]) => {
        setArticles(artRes.data);
        setCategories(catRes.data);
      })
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    let list = articles;
    if (selectedCategory) list = list.filter((a) => a.category === selectedCategory);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (a) =>
          a.title.toLowerCase().includes(q) ||
          a.summary.toLowerCase().includes(q) ||
          (a.tags || []).some((t) => t.toLowerCase().includes(q))
      );
    }
    return list;
  }, [articles, selectedCategory, search]);

  return (
    <div className="space-y-6 max-w-6xl mx-auto p-6 lg:p-8">
      <div className="space-y-2">
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight">Help Centre</h1>
        <p className="text-base text-muted-foreground">
          Browse articles, search for what you need, or ask Guardian AI.
        </p>
      </div>

      {/* Search bar */}
      <div className="relative">
        <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search the Help Centre…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10 h-12 text-base"
          data-testid="help-search-input"
        />
      </div>

      {/* AI Help quick-prompt */}
      <Card
        onClick={() => navigate('/help/ai')}
        className={`cursor-pointer hover:border-primary/60 transition-colors ${isPro ? '' : 'opacity-90'}`}
        data-testid="help-ai-quick-prompt"
      >
        <CardContent className="py-5 flex items-center gap-4">
          <div className="w-12 h-12 rounded-md bg-primary/10 border border-primary/30 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-6 h-6 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold">Ask Guardian AI</p>
            <p className="text-sm text-muted-foreground">
              {isPro ? 'Get an instant answer from your Help Centre, powered by AI.' : 'Pro feature — upgrade to ask AI any "how do I…" question.'}
            </p>
          </div>
          <ChevronRight className="w-5 h-5 text-muted-foreground flex-shrink-0" />
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6">
        {/* Categories sidebar */}
        <aside>
          <p className="label-overline text-xs uppercase tracking-widest text-muted-foreground mb-3">Categories</p>
          <div className="space-y-1">
            <button
              onClick={() => setSelectedCategory(null)}
              className={`w-full text-left px-3 py-2 rounded-md text-sm flex items-center justify-between hover:bg-secondary/60 ${selectedCategory === null ? 'bg-secondary text-foreground font-medium' : 'text-muted-foreground'}`}
              data-testid="help-category-all"
            >
              <span>All articles</span>
              <span className="text-xs">{articles.length}</span>
            </button>
            {categories.map((c) => (
              <button
                key={c.category}
                onClick={() => setSelectedCategory(c.category)}
                className={`w-full text-left px-3 py-2 rounded-md text-sm flex items-center justify-between hover:bg-secondary/60 ${selectedCategory === c.category ? 'bg-secondary text-foreground font-medium' : 'text-muted-foreground'}`}
                data-testid={`help-category-${c.category.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <span>
                  <span className="mr-1.5">{CATEGORY_ICONS[c.category] || '📄'}</span>
                  {c.category}
                </span>
                <span className="text-xs">{c.count}</span>
              </button>
            ))}
          </div>
        </aside>

        {/* Article cards */}
        <div className="space-y-3">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading articles…
            </div>
          ) : filtered.length === 0 ? (
            <Card>
              <CardContent className="py-10 text-center text-muted-foreground">
                <BookOpen className="w-8 h-8 mx-auto mb-3 opacity-50" />
                <p>No articles match your search.</p>
                <p className="text-sm mt-1">Try a different keyword, or ask Guardian AI above.</p>
              </CardContent>
            </Card>
          ) : (
            filtered.map((a) => (
              <Card
                key={a.id}
                onClick={() => navigate(`/help/${a.slug}`)}
                className="cursor-pointer hover:border-primary/40 transition-colors"
                data-testid={`help-article-card-${a.slug}`}
              >
                <CardContent className="py-4 flex items-start gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant="outline" className="text-xs border-border text-muted-foreground">
                        {a.category}
                      </Badge>
                    </div>
                    <p className="font-semibold mb-1">{a.title}</p>
                    <p className="text-sm text-muted-foreground line-clamp-2">{a.summary}</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-1" />
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>

      {/* Contact support footer */}
      <Card className="border-dashed">
        <CardContent className="py-5 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <LifeBuoy className="w-5 h-5 text-primary" />
            <div>
              <p className="font-medium">Can't find what you need?</p>
              <p className="text-sm text-muted-foreground">Send our support team a message.</p>
            </div>
          </div>
          <Button
            onClick={() => window.dispatchEvent(new CustomEvent('guardian:open-support'))}
            data-testid="help-contact-support-btn"
            variant="outline"
          >
            Contact Support
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};


/**
 * Article detail page.
 */
export const HelpArticlePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { formatDate } = useSettings();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    helpApi
      .getArticle(slug)
      .then((res) => setArticle(res.data))
      .catch((err) => setError(err.response?.data?.detail || 'Article not found'))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading article…
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <button onClick={() => navigate('/help')} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4">
          <ArrowLeft className="w-4 h-4" /> Back to Help Centre
        </button>
        <Card>
          <CardContent className="py-10 text-center">
            <p className="font-medium text-foreground">Article not found</p>
            <p className="text-sm text-muted-foreground mt-1">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto p-6 lg:p-8 space-y-6">
      <button
        onClick={() => navigate('/help')}
        className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"
        data-testid="help-back-btn"
      >
        <ArrowLeft className="w-4 h-4" /> Back to Help Centre
      </button>

      <div className="space-y-2">
        <Badge variant="outline" className="text-xs">{article.category}</Badge>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" data-testid="help-article-title">
          {article.title}
        </h1>
        <p className="text-sm text-muted-foreground">Updated {formatDate(article.updated_at)}</p>
      </div>

      <Card>
        <CardContent className="py-6 px-6 lg:px-8">
          <Markdown source={article.body_markdown} />
        </CardContent>
      </Card>

      <Card className="border-dashed">
        <CardContent className="py-5 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <LifeBuoy className="w-5 h-5 text-primary" />
            <p className="text-sm text-muted-foreground">Still stuck? Our team is one click away.</p>
          </div>
          <Button
            onClick={() => window.dispatchEvent(new CustomEvent('guardian:open-support'))}
            data-testid="help-article-contact-support-btn"
            variant="outline"
            size="sm"
          >
            Contact Support
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};


/**
 * AI Help page — natural-language Q&A over the help articles. Pro-gated.
 */
export const AIHelpPage = () => {
  const navigate = useNavigate();
  const { isPro, loading: planLoading, onUpgrade } = usePlan();
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState(null);
  const [citations, setCitations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const ask = async (q) => {
    if (!q.trim()) return;
    if (!isPro) {
      onUpgrade();
      return;
    }
    setLoading(true);
    setError(null);
    setAnswer(null);
    setCitations([]);
    try {
      const res = await helpApi.askAI(q.trim());
      setAnswer(res.data.answer);
      setCitations(res.data.citations || []);
    } catch (err) {
      if (err.response?.status === 402) {
        onUpgrade();
        return;
      }
      setError(err.response?.data?.detail || 'AI is unavailable right now. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const suggestions = [
    'How do I bulk-import assets?',
    'What is the difference between deactivate and delete?',
    'How do I set up a preventative maintenance schedule?',
    'How do I attach a Task Library template to a PM?',
  ];

  return (
    <div className="max-w-3xl mx-auto p-6 lg:p-8 space-y-6">
      <button
        onClick={() => navigate('/help')}
        className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"
        data-testid="help-ai-back-btn"
      >
        <ArrowLeft className="w-4 h-4" /> Back to Help Centre
      </button>

      <div className="flex items-center gap-3">
        <Sparkles className="w-7 h-7 text-primary" />
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Ask Guardian AI</h1>
      </div>
      <p className="text-base text-muted-foreground">
        Ask anything about Guardian. Answers come from our Help Centre — with citations.
      </p>

      <Card>
        <CardContent className="py-5 space-y-3">
          <Input
            placeholder="e.g. How do I bulk-apply maintenance to all excavators?"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') ask(question);
            }}
            className="h-12 text-base"
            data-testid="help-ai-input"
          />
          <div className="flex flex-wrap gap-2">
            {suggestions.map((s) => (
              <button
                key={s}
                onClick={() => {
                  setQuestion(s);
                  ask(s);
                }}
                className="text-xs px-3 py-1.5 rounded-md border border-border bg-secondary/40 hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
                data-testid={`help-ai-suggestion-${s.length}`}
              >
                {s}
              </button>
            ))}
          </div>
          <Button onClick={() => ask(question)} disabled={loading || planLoading || !question.trim()} className="w-full sm:w-auto" data-testid="help-ai-ask-btn">
            {(loading || planLoading) && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {planLoading ? 'Loading…' : isPro ? 'Ask Guardian AI' : 'Upgrade to ask'}
          </Button>
        </CardContent>
      </Card>

      {error && (
        <Card className="border-destructive/40 bg-destructive/5">
          <CardContent className="py-4 text-sm text-destructive">{error}</CardContent>
        </Card>
      )}

      {answer && (
        <Card data-testid="help-ai-answer">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-primary" /> Answer
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Markdown source={answer} />
            {citations.length > 0 && (
              <div className="mt-6 pt-4 border-t border-border">
                <p className="label-overline text-xs uppercase tracking-widest text-muted-foreground mb-2">Sources</p>
                <div className="flex flex-wrap gap-2">
                  {citations.map((c) => (
                    <button
                      key={c.slug}
                      onClick={() => navigate(`/help/${c.slug}`)}
                      className="text-xs px-3 py-1.5 rounded-md border border-primary/30 bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                      data-testid={`help-ai-citation-${c.slug}`}
                    >
                      {c.title}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};
